import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn, FormGroup, FormBuilder, ReactiveFormsModule} from '@angular/forms';
import { CmElement, CmHelp, CmSection, CmTooltip, ContentType } from '../shared/model/cm/managed-content.model';
import { ManagedContentService } from '../shared/service/managed-content.service';
import { EEOI_APP_ROUTES, GlobalConstants } from '../shared/global-constants';
import { UserSessionService } from "../shared/service/user-session.service";
import { LifeStructure } from '../shared/model/session/life-structure';
import { DeactivateGuard } from '../deactivate.guard';
import { fromEvent, Observable, of, Subscription } from 'rxjs';
import { PrismDropDown } from '../shared/model/prism-dropdown';
import { CoverageMhs } from '../shared/model/session/coverage-mhs';
import { ApplicantMhs } from '../shared/model/session/applicant-mhs';
import { AppInsightsApiService} from "../shared/service/app-insights-api.service";
import { ApplicationService } from '../shared/service/application.service';
import { AppInsightsLoggerService } from '../shared/service/app-insights-logger.service';
import { PartialSaveResp } from '../shared/model/partial-save-resp';
import { ViewportScroller } from '@angular/common';
import { PagetitleService } from '../shared/service/pagetitle.service';
import { AuthGuardService } from '../shared/service/auth-guard.service';
import { ApplicantType } from '../shared/model/ui/applicant-type';
import { RadioButton } from '../shared/model/session/radio-button';
import { Product } from '../shared/model/ui/product';
import { GroupEdp } from '../shared/model/group-edp';
import { Subproduct } from '../shared/model/ui/subProduct';
import { ProductLineEdp } from '../shared/model/product-line-edp';
import { ProductEdp } from '../shared/model/product-edp';
import { WindowRef } from '../shared/service/window.service';
import { ModalService } from '../shared/service/modal.service';
import { FormUtils } from '../shared/helper/form-utils';
import { GoogleAnalyticsService } from '../shared/service/google-analytics.service';
import { CmDialog } from '../shared/model/cm-dialog';
import { CmFormElement } from '../shared/model/cm-form-element';
import { FormControlUtilitiesService } from '../shared/service/form-control-utilities.service';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { ContinueCancelComponent } from '../continue-cancel/continue-cancel.component';
import { DisabilityRadioComponent } from '../disability-radio/disability-radio.component';
import { LifeScheduleRadioComponent } from '../life-schedule-radio/life-schedule-radio.component';
import { LifeScheduleDollarComponent } from '../life-schedule-dollar/life-schedule-dollar.component';
import { LifeScheduleSalaryComponent } from '../life-schedule-salary/life-schedule-salary.component';

export class lclProducts {
  productLine: string;
  productName: string;
  numberOfProducts: number;
}

@Component({
  selector: 'app-coverage',
  standalone: true,
  imports: [ReactiveFormsModule, ContinueCancelComponent, DisabilityRadioComponent, LifeScheduleRadioComponent, LifeScheduleDollarComponent, LifeScheduleSalaryComponent],
  templateUrl: './coverage.component.html',
  styleUrls: ['./coverage.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CoverageComponent implements OnInit, DeactivateGuard, AfterViewInit {
  @Input() parentForm: UntypedFormGroup;
  @Output() cancelEmitter = new EventEmitter<void>();

  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;
  public innerWidth: number = window.innerWidth;
  public coverageTooltipPosition: string = 'top';

  private cancelContent: CmHelp = this.cmService.getContentById(ContentType.HELP, "lose-data-warning");
  public modalContentHeading: string = this.cancelContent.title;
  public modalContentBody: string = FormUtils.sanitize(this.cancelContent.body);

  public products: Product[] = [];
  public productLines: ProductLineEdp[] = [];
  public allProducts: lclProducts[] = [];

  public applicantType: string;
  readonly ApplicantType = ApplicantType;

  private resetSubmitted: boolean = false;
  private submitted: boolean = false;
  public submittedClicked: boolean = false;

  public checkboxFormValid: boolean = true;

  public edpGroupData: GroupEdp;
  isEditFromReview = false;
  /**
   * True while an edit-mode save is in flight to the backend. Used to:
   *  - disable the Save button to prevent double-submits,
   *  - guard navigation so we only return to auth-elec-sign after the
   *    backend acknowledged the new coverage.
   */
  public isSavingEdit: boolean = false;

  backButtonDialogContent: CmDialog = {
    id: "backModal",
    heading: this.modalContentHeading,
    body: this.modalContentBody,
    size: "medium",
  };

  public readonly pageIllustrationUrl = 'assets/Illustration-Footer-Base-Mountains.svg';

  coverageForm: FormGroup;

  //Display items
  formSection: CmSection;
  selectCoverageElement: CmElement;
  selectCoverageToolTip: CmTooltip;
  selectCoverageToolTipTitle: string;
  selectCoverageToolTipDescription: string;
  coverageElement: CmElement;

  // GA4 Custom events
  public submitCoverages = {
    action: "button_click",
    category: "coverage",
    name: "submit",
  }

  public cancelCoverages = {
    action: "button_click",
    category: "coverage",
    name: "cancel",
  }

  public continueButtonProps = {
    keyId: 'continue-button',
    size: 'default',
    text: 'Continue',
    type: 'submit',
    variant: 'primary'
  }

  public cancelButtonProps = {
    keyId: 'cancel-button',
    size: 'default',
    text: 'Cancel',
    type: 'button',
    variant: 'text'
  }

  public readonly multiplesOfSalary: Array<PrismDropDown> = GlobalConstants.SALARY_MULTIPLES_PRISM;

  public RADIO_BUTTON_OPTIONS: Array<RadioButton>;

  public readonly APPLICANT_TYPES = GlobalConstants.APPLICANT_TYPES;

  constructor(private router: Router,
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    private cmService: ManagedContentService,
    private userSessionService: UserSessionService,
    private appInsightsApiService: AppInsightsApiService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private scroller: ViewportScroller,
    private pagetitleService: PagetitleService,
    private authGuardService: AuthGuardService,
    private modalService: ModalService,
    private windowService: WindowRef,
    private applicationService: ApplicationService,
    private appInsightsLoggerService: AppInsightsLoggerService,
    public formControlUtilitiesService: FormControlUtilitiesService) {
  }

  ngOnInit() {
    if(this.userSessionService.data.sessionEnded){
      this.router.navigate(['/instructions']);
    }
    this.applicantType = this.userSessionService.data.applicantType;
    this.edpGroupData = this.userSessionService.data.edpGroupData;

    //get productLines for applicant
    this.productLines = this.getProductLinesByApplicantType(this.applicantType);

    //get a distinct list of all product names (and count)
    this.fillAllProducts(this.productLines);

    this.fillProducts();

    const isEditFromState = history.state && history.state.editCoverage === true;
    const isEditFromQuery = this.activatedRoute.snapshot.queryParamMap.get('mode') === 'edit';
    this.isEditFromReview = isEditFromState || isEditFromQuery;

    // Clear the query params from the URL after detection to keep URL clean
    if (isEditFromQuery) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {},
        replaceUrl: true
      });
    }

    this.formSection = this.cmService.getContentById(ContentType.SECTION, 'coverage');//default title
    this.coverageElement = this.cmService.getContentById(ContentType.ELEMENT, 'coverage-error');
    this.selectCoverageElement = this.cmService.getContentById(ContentType.ELEMENT, 'select-coverage');
    this.selectCoverageToolTip = this.cmService.getContentById(ContentType.TOOLTIP, this.selectCoverageElement.tooltip);
    this.selectCoverageToolTipTitle = this.selectCoverageToolTip.title;
    this.selectCoverageToolTipDescription = this.selectCoverageToolTip.plainText;

    if(this.products){
      this.coverageForm = new FormGroup({});
    }

    //add checkboxes to the form
    for(let pp of this.products){
      this.coverageForm.addControl(pp.key, this.fb.control(''));
      this.coverageForm.get(pp.key).setValue(false);
    }

    if(this.coverageForm){
      this.coverageForm.setErrors( { requireCheckboxesToBeChecked: true });
    }

    if(this.windowService){
      this.windowService.nativeDocument
        .querySelector('body')
        .addEventListener('click', (event: Event) => this.onClick(event));
    }

    //auto-check the box if there is only one product
    if(this.products.length == 1 ){
      this.coverageForm.get(this.products[0].key).setValue(true);
      if(this.products[0].productLine === 'LIFE'){
        this.coverageForm.addControl(this.products[0].key + '_schedule', this.fb.control('', [Validators.required]));
      } else {//LTD or STD
        if(this.products[0].subProducts.length > 0){
          this.coverageForm.addControl(this.products[0].key + '_subProduct', this.fb.control('', [Validators.required]));
        }
      }
    }

    this.restoreCoverageSelections();

    this.resizeObservable$ = fromEvent(window, 'resize')
    this.resizeSubscription$ = this.resizeObservable$.subscribe( evt => {
      this.innerWidth = window.innerWidth;
      this.changeTooltipPosition();
    });
  }

  ngAfterViewInit() {
    this.pagetitleService.setPageTitle('Coverage');
    this.appInsightsApiService.endNavigationEvent(this.router.url);

    this.innerWidth = window.innerWidth;
    this.changeTooltipPosition();

    setTimeout( ()=>{
      this.onScrollToTop();
      }, 1);
  }

  goBack() {
    // Mark as safe navigation to bypass cancel-modal flow when returning from edit mode.
    this.submitted = true;
    // Clear success toast flags since we're navigating back without saving
    this.userSessionService.data['showCoverageSavedToast'] = false;
    sessionStorage.removeItem('showCoverageSavedToast');
    this.router.navigate(['/review']);
  }

  saveAndGoToReview() {
    // Defend against double-click while a backend save is already in flight.
    if (this.isSavingEdit) {
      return;
    }
    this.onSubmit();
  }

  private restoreCoverageSelections(): void {
    const savedCoverages: CoverageMhs[] = this.userSessionService.data.mhsCoverages || [];
    if (!savedCoverages.length || !this.coverageForm) {
      return;
    }

    for (const coverage of savedCoverages) {
      const matchedProduct = this.findMatchingProduct(coverage);
      if (!matchedProduct) {
        continue;
      }

      this.checkboxChecked({ detail: { checked: true } } as unknown as Event, matchedProduct.key);

      const savedSchedule = (coverage.lifeCoverageStructure || '').toUpperCase();
      if (savedSchedule && this.coverageForm.contains(matchedProduct.key + '_schedule')) {
        this.coverageForm.get(matchedProduct.key + '_schedule').setValue(savedSchedule);
      }

      // Pre-seed the amount control BEFORE the child <app-life-schedule-dollar>
      // or <app-life-schedule-salary> mounts. The child's ngOnInit is idempotent
      // (it preserves any existing control), so when it runs it will see the
      // pre-populated value and the input will render with the amount on the
      // very first paint instead of flashing blank for ~75ms.
      if (matchedProduct.productLine === 'LIFE') {
        const additionalAmount = coverage.additionalAmount || '';
        if (additionalAmount.includes('x Annual Earnings')) {
          const multipleKey = matchedProduct.key + '_multipleOfSalary';
          const normalizedValue = this.normalizeMultipleOfSalaryValue(additionalAmount);
          if (!this.coverageForm.contains(multipleKey)) {
            this.coverageForm.addControl(multipleKey, this.fb.control(normalizedValue, [Validators.required]));
          } else {
            this.coverageForm.get(multipleKey).setValue(normalizedValue);
          }
        } else if (additionalAmount) {
          const dollarKey = matchedProduct.key + '_dollar';
          if (!this.coverageForm.contains(dollarKey)) {
            this.coverageForm.addControl(dollarKey, this.fb.control(additionalAmount, [Validators.required]));
          } else {
            this.coverageForm.get(dollarKey).setValue(additionalAmount);
          }
        }
      }
    }

    this.checkIfCheckboxFormValid();

    // Child components add nested controls; defer value restoration until controls exist.
    this.applyDeferredCoverageSelections(savedCoverages);
  }

  /**
   * Saved coverage strings come back as "<number> x Annual Earnings" where
   * <number> can be "2" or "2.0" (depending on which UI version stored it).
   * The <prism-ui-select> dropdown options have canonical values "0.5", "1.0",
   * "1.5"... "8.0", so we normalize to that form when restoring. If we can't
   * find a match we return the raw token so the user still sees something.
   */
  private normalizeMultipleOfSalaryValue(additionalAmount: string): string {
    const raw = (additionalAmount || '').split(' ')[0];
    if (!raw) {
      return '';
    }
    // Direct match (e.g. "2.0").
    let match = this.multiplesOfSalary.find(opt => opt.value === raw);
    if (match) {
      return match.value;
    }
    // Numeric match (e.g. raw "2" -> value "2.0").
    const rawNum = parseFloat(raw);
    if (!isNaN(rawNum)) {
      match = this.multiplesOfSalary.find(opt => parseFloat(opt.value) === rawNum);
      if (match) {
        return match.value;
      }
    }
    return raw;
  }

  private applyDeferredCoverageSelections(savedCoverages: CoverageMhs[], attempt: number = 0): void {
    let hasPendingControls = false;

    for (const coverage of savedCoverages) {
      const matchedProduct = this.findMatchingProduct(coverage);
      if (!matchedProduct) {
        continue;
      }

      if (matchedProduct.productLine === 'LIFE') {
        const scheduleControl = this.coverageForm.get(matchedProduct.key + '_schedule');
        if (scheduleControl) {
          const savedSchedule = (coverage.lifeCoverageStructure || '').toUpperCase();
          if (savedSchedule === 'DOLLAR' || savedSchedule === 'SALARY') {
            scheduleControl.setValue(savedSchedule);
          }
        } else {
          hasPendingControls = true;
        }

        const additionalAmount = coverage.additionalAmount || '';
        if (additionalAmount.includes('x Annual Earnings')) {
          const multipleControl = this.coverageForm.get(matchedProduct.key + '_multipleOfSalary');
          if (multipleControl) {
            multipleControl.setValue(this.normalizeMultipleOfSalaryValue(additionalAmount));
          } else {
            hasPendingControls = true;
          }
        } else if (additionalAmount) {
          const dollarControl = this.coverageForm.get(matchedProduct.key + '_dollar');
          if (dollarControl) {
            dollarControl.setValue(additionalAmount);
          } else {
            hasPendingControls = true;
          }
        }
      }

      if (matchedProduct.subProducts && matchedProduct.subProducts.length > 1) {
        const subProductControl = this.coverageForm.get(matchedProduct.key + '_subProduct');
        const matchedSubProduct = matchedProduct.subProducts.find(sp =>
          sp.benefitPercent === coverage.benefitPercent &&
          sp.benefitWaitingPeriod === coverage.benefitWaitingPeriod &&
          sp.cola === coverage.cola
        );

        if (subProductControl && matchedSubProduct) {
          subProductControl.setValue(matchedSubProduct.displayName);
        } else if (!subProductControl) {
          hasPendingControls = true;
        }
      }
    }

    this.coverageForm.updateValueAndValidity();

    if (hasPendingControls && attempt < 5) {
      setTimeout(() => this.applyDeferredCoverageSelections(savedCoverages, attempt + 1), 75);
    }
  }

  private findMatchingProduct(coverage: CoverageMhs): Product | undefined {
    return this.products.find(product => {
      if (product.productLine !== coverage.productLine) {
        return false;
      }

      if (product.productName === coverage.productName) {
        return true;
      }

      // Some saved names may include concatenated benefit text.
      return coverage.productName?.startsWith(product.productName);
    });
  }

  changeTooltipPosition(){
    if(this.innerWidth < 735){
      this.coverageTooltipPosition = 'left';
    } else {
      this.coverageTooltipPosition = 'top';
    }
  }

  fillProducts(){

    let keyNumber: number = 1;
    for(let ap of this.allProducts){
      let oneProduct = new Product();

      oneProduct.key = 'Product_' + keyNumber.toString();
      oneProduct.productLine = ap.productLine;

      if(ap.numberOfProducts == 1){
        //check for concat subproduct name
        if(ap.productLine === 'LTD' || ap.productLine === 'STD'){
          oneProduct.productName = this.getProductName(ap.productName);
          oneProduct.subProducts = this.getOneSubProduct(ap.productName)
        } else {//LIFE
          oneProduct.productName = ap.productName;
        }

      } else if(ap.numberOfProducts > 1){
        if(ap.productLine === 'LTD' || ap.productLine === 'STD'){
          oneProduct.productName = ap.productName;

          //check for radio buttons (subproducts)
          if(this.doesProductHaveTwoOrMoreSubProducts(ap.productName)){
            oneProduct.subProducts = this.getSubProducts(ap.productName, oneProduct.key);
          }
        }
      }

      //life schedule?
      if(ap.productLine === 'LIFE'){
        oneProduct.productSchedule = this.getProductSchedule(ap.productName);
      }

      this.products.push(oneProduct);
        keyNumber++;
    }

    //reorder products
    this.reorderProducts();
  }

  doesProductHaveTwoOrMoreSubProducts(productName: string){
    let count = 0;
    for(let pl of this.productLines){
      for(let pp of pl.products){
        if(pp.productName === productName){
          if(pp.benefitPercent != undefined || pp.benefitPercent != null){
            count++;
            if(count > 1){
              return true;
            }
          }
        }
      }
    }
    count = 0;

    return false;
  }

  getProductSchedule(productName: string){
    let returnString: string = null;

    for(let pl of this.productLines){
      for(let pp of pl.products){
        if(pp.productName === productName){
          returnString = pp.productSchedule
        }
      }
    }

    return returnString;
  }

  //This function is used to concatenate the Product name if there is only one subProduct name for a given Product
  getOneSubProduct(productName: string){
    let subProducts: Subproduct[] = [];
    for(let pl of this.productLines){
      for(let pp of pl.products){
        if(pp.productName === productName){
          if(pp.benefitPercent != undefined || pp.benefitPercent != null){

            let oneSubProduct = new Subproduct();

            oneSubProduct.key = 'oneSubProduct'
            oneSubProduct.displayName = productName;

            oneSubProduct.benefitPercent = pp.benefitPercent;
            oneSubProduct.benefitWaitingPeriod = pp.benefitWaitingPeriod;
            oneSubProduct.cola = pp.cola;

            subProducts.push(oneSubProduct);
          }
        }
      }
    }

    return subProducts;
  }

  //subProducts are radio buttons if there is one or more.
  //An example of a subProduct is "45% Benefit, 60-Day Benefit Waiting Period with COLA"
  getSubProducts(productName: string, key: string){
    let subProducts: Subproduct[] = [];

    for(let pl of this.productLines){
      for(let pp of pl.products){
        if(pp.productName === productName){
          if(pp.benefitPercent != undefined || pp.benefitPercent != null){

            let oneSubProduct = new Subproduct();
            let subProductIndex = 1;
            oneSubProduct.key = key + '_subProduct' + '_' + subProductIndex.toString();
            oneSubProduct.displayName = this.getConcatProductName(pp.productName, pp.benefitWaitingPeriod, pp.benefitPercent, pp.cola);

            oneSubProduct.benefitPercent = pp.benefitPercent;
            oneSubProduct.benefitWaitingPeriod = pp.benefitWaitingPeriod;
            oneSubProduct.cola = pp.cola;

            if(subProducts.length >= 1){
              let prod: Subproduct = subProducts[subProducts.length -1];//this is the last product
              let indexNum = prod.key.charAt(prod.key.length-1);
              let newIndexNum = Number(indexNum) + 1;
              let newKey: string = prod.key.slice(0, -1) + newIndexNum.toString();
              oneSubProduct.key = newKey;
            }

            subProducts.push(oneSubProduct);
          }
        }
      }
    }

    return subProducts;
  }

  getProductName(productName: string){
    let returnString: string = productName;

    for(let pl of this.productLines){
      for(let pp of pl.products){
        if(pp.productName === productName){
          if(pp.benefitPercent != undefined || pp.benefitPercent != null){
            returnString = returnString + ' - ' + this.getConcatProductName(pp.productName, pp.benefitWaitingPeriod, pp.benefitPercent, pp.cola);
          }
        }
      }
    }

    return returnString;
  }

  fillAllProducts(productLines: ProductLineEdp[]){
    for(let pl of productLines){
      //iterate over the products in each product line
      let products: ProductEdp[] = [];
      products = pl.products
      for(let p of products){
        this.addToAllProducts(p.productName, pl.productLine);
      }
    }

    //update the count to > 1 for any products that exist more than once
    let cnt: number = 0;
    let index: number = 0;
    for(let ap of this.allProducts){
      for(let pl of productLines){
        let products: ProductEdp[] = [];
        products = pl.products
        for(let p of products){
          if(p.productName === ap.productName){
            cnt++;
          }
        }
      }
      let oneProduct: lclProducts = new lclProducts();
      oneProduct.productLine = this.allProducts[index].productLine;
      oneProduct.productName = ap.productName;
      oneProduct.numberOfProducts = cnt;

      //update allProducts
      this.allProducts[index] = oneProduct;
      //reset count and increase index;
      cnt = 0;
      index++;
    }
  }

  getProductLinesByApplicantType(applicantType: string){
    let productLines: ProductLineEdp[]
    for(let apps of this.edpGroupData.productsByApplicantType){
      if(applicantType.toLowerCase() === apps.applicantType.toLowerCase()){
        productLines = apps.productLines;
      }
    }
    return productLines;
  }

  reorderProducts(){
    this.products.sort(this.compareProducts);
  }

  compareProducts(productOne: Product, productTwo: Product){
    if(productOne.productLine == productTwo.productLine){
      return 0;
    } else {
      if(productOne.productLine == 'LIFE'){
        return -1;
      } else if(productOne.productLine == 'LTD'){
        if(productTwo.productLine == 'LIFE'){
          return 1;
        } else if(productTwo.productLine == 'STD'){
          return -1;
        }
      } else if(productOne.productLine == 'STD'){
        return 1;
      }
    }
  }

  addToAllProducts(productName: string, productLine: string){
    if(!this.checkIfProductExistsInAllProducts(productName)){
      //add to allProducts
      let oneProduct = new lclProducts();
      oneProduct.numberOfProducts = 1;
      oneProduct.productLine = productLine;
      oneProduct.productName = productName;
      this.allProducts.push(oneProduct);
    }
  }

  getConcatProductName(productName: string, benefitWaitingPeriod: string, benefitPercent: string, cola: boolean){
    let returnString: string = '';

    if(benefitPercent != undefined && benefitPercent != ""){
      benefitPercent = benefitPercent + "% Benefit";
    } else {
      benefitPercent = '';
    }

    if(benefitWaitingPeriod != undefined){
      benefitWaitingPeriod = benefitWaitingPeriod + "-Day Benefit Waiting Period";
    } else {
      benefitWaitingPeriod = '';
    }

    let spaceOne: string;
    if(benefitPercent != '' && benefitWaitingPeriod != ''){
      spaceOne = ", ";
    } else {
      spaceOne = "";
    }

    let spaceTwo: string;
    if(benefitWaitingPeriod != '' && benefitWaitingPeriod != ''){
      spaceTwo = " ";
    } else {
      spaceTwo = "";
    }

    let benCola: string = '';
    if(cola){
      benCola = "with COLA";
    } else {
      benCola = '';
    }

    returnString = benefitPercent + spaceOne + benefitWaitingPeriod + spaceTwo + benCola;

    return returnString.trim();
  }

  checkIfProductExistsInAllProducts(productName: string){
    let returnBool: boolean = false;

    for(let prod of this.allProducts){
      if(prod.productName === productName){
        returnBool = true;
        break;
      }
    }

    return returnBool;
  }

  private checkFormsAreValid(){
    let returnBool: boolean = false;

    if(this.coverageForm.valid){
      returnBool = true;
    }

    return returnBool;
  }

  onSubmit(){

    this.submittedClicked = true;

    this.coverageForm.markAllAsTouched();
    this.coverageForm.updateValueAndValidity();
    this.checkIfCheckboxFormValid();

    if(this.checkboxFormValid && this.coverageForm.valid){
      if(this.checkFormsAreValid()){

        var mhsCoverages: CoverageMhs[] = [];

        //iterate over the form and look for checkboxes that were checked
        Object.keys(this.coverageForm.controls).forEach(key => {

          const control = this.coverageForm.controls[key];

          if (control.value === true) {

            let oneCoverage = new CoverageMhs();

            for(let p of this.products){
              if(p.key === key){
                oneCoverage.planName = p.productLine;
                oneCoverage.planCode = p.productLine;
                oneCoverage.productLine = p.productLine;
                oneCoverage.productName = p.productName;
                if(p.productLine === 'LIFE'){
                  if(this.coverageForm.get(key + '_schedule').value.toUpperCase() === 'DOLLAR'){
                    oneCoverage.lifeCoverageStructure = LifeStructure.Dollar.toUpperCase();
                  } else if(this.coverageForm.get(key + '_schedule').value.toUpperCase() === 'SALARY'){
                    oneCoverage.lifeCoverageStructure = LifeStructure.Salary.toUpperCase();
                  }

                  if(oneCoverage.lifeCoverageStructure === LifeStructure.Dollar.toUpperCase()){
                    oneCoverage.additionalAmount = this.coverageForm.get(key + '_dollar').value;
                  } else if(oneCoverage.lifeCoverageStructure === LifeStructure.Salary.toUpperCase()){
                    oneCoverage.additionalAmount = this.coverageForm.get(key + '_multipleOfSalary').value + ' x Annual Earnings';
                  }

                } else if(p.productLine === 'LTD' || p.productLine === 'STD'){
                  if(p.subProducts){
                    if(p.subProducts.length > 0){
                      if(p.subProducts.length == 1){//one product
                        if(p.subProducts[0].key === 'oneSubProduct'){
                          oneCoverage.productName = p.subProducts[0].displayName;
                          oneCoverage.benefitPercent = p.subProducts[0].benefitPercent;
                          oneCoverage.benefitWaitingPeriod = p.subProducts[0].benefitWaitingPeriod;
                          oneCoverage.cola = p.subProducts[0].cola;
                        }
                      } else if(p.subProducts.length > 1){//radio buttons
                        let selectedSubProduct = this.coverageForm.get(p.key + '_subProduct').value;

                        for(let sp of p.subProducts){
                          if(sp.displayName === selectedSubProduct){

                            oneCoverage.benefitPercent = sp.benefitPercent;
                            oneCoverage.benefitWaitingPeriod = sp.benefitWaitingPeriod;
                            oneCoverage.cola = sp.cola;

                          }
                        }
                      }
                    }
                  }
                }
                mhsCoverages.push(oneCoverage);
              }
            }
          }

        });

        this.userSessionService.data.mhsCoverages = mhsCoverages;

        // Keep the in-memory MHS application payload in sync with the latest
        // coverage selections. Without this the final PDF (generated server-side
        // from `mhsApplication.applicant.coverage`) would still reflect the
        // first-time coverage when the user edited from auth-elec-sign.
        this.syncCoverageIntoMhsApplication(mhsCoverages);

        this.submitted = true;
        
        // Check if continuing from confirmation with non-employee applicant
        const isContinuing = this.userSessionService.data.isContinuingFromConfirmation;
        const isNonEmployee = this.applicantType?.toLowerCase() !== 'employee';
        
        if (isContinuing && isNonEmployee) {
          // For spouse/child when continuing from confirmation, call partialapplication first
          this.savePartialApplicationAndNavigate();
        } else {
          // Normal flow - navigate to employee-information
          const destinationRoute = this.isEditFromReview ? EEOI_APP_ROUTES.AUTH_ELEC_SIGN : EEOI_APP_ROUTES.EMPLOYEE_INFO;
        const navigationState = this.isEditFromReview ? { showSuccess: true } : undefined;
        if (this.isEditFromReview) {
          this.userSessionService.data['showCoverageSavedToast'] = true;
          sessionStorage.setItem('showCoverageSavedToast', 'true');
        }
        this.router.navigate([destinationRoute], navigationState ? { state: navigationState } : undefined);
          this.appInsightsApiService.startNavigationEvent(destinationRoute.toString());
        }
        this.resetSubmitted = true;

        // First-time flow: just advance to employee-info (no backend write
        // needed here - applicant-info will push the coverage later).
        if (!this.isEditFromReview) {
          this.router.navigate([EEOI_APP_ROUTES.EMPLOYEE_INFO]);
          this.appInsightsApiService.startNavigationEvent(EEOI_APP_ROUTES.EMPLOYEE_INFO.toString());
          this.resetSubmitted = true;
        } else {
          // Edit flow from auth-elec-sign: persist the change to the backend
          // BEFORE navigating, so the PDF generated later includes the new
          // coverage. Navigation happens in the success / error handlers.
          this.persistEditedCoverage();
          this.resetSubmitted = true;
        }
      }
    }

    if (!this.resetSubmitted && this.coverageForm.valid) {
      this.submitted = false;
      this.resetSubmitted = false;
    }
  }

  async savePartialApplicationAndNavigate() {
    // Set formNumber in the application object (required by API)
    this.userSessionService.data.mhsApplication.formNumber = this.userSessionService.data.formNumber;
    this.userSessionService.data.mhsApplication.companyId = this.userSessionService.data.companyId;
    
    // Ensure preserved employee data is included in the payload
    if (this.userSessionService.data.preservedEmployeeData?.employeeMhs) {
      this.userSessionService.data.mhsApplication.employee = this.userSessionService.data.preservedEmployeeData.employeeMhs;
    }
    
    // Ensure applicant state is set
    if (!this.userSessionService.data.mhsApplication.applicant) {
      this.userSessionService.data.mhsApplication.applicant = new ApplicantMhs();
    }
    if (this.userSessionService.data.state) {
      this.userSessionService.data.mhsApplication.applicant.stateOfResidence = this.userSessionService.data.state;
    }
    
    let resp = new PartialSaveResp();
    try {
      const result = await this.applicationService.submitPartialCloudApplication(
        this.userSessionService.data.formNumber, 
        this.userSessionService.data.mhsApplication
      );
      resp = result;
      
      if (resp.httpStatusCode === 201) {
        this.appInsightsLoggerService.log('info', 'Partial application saved successfully. Component: CoverageComponent, mhsId/caseId: ' + result.data.mhsId, '');
        this.userSessionService.data.mhsId = result.data.mhsId;
        
        // Navigate to applicant-information for spouse/child
        this.router.navigate([EEOI_APP_ROUTES.APPLICANT_INFO]);
        this.appInsightsApiService.startNavigationEvent([EEOI_APP_ROUTES.APPLICANT_INFO].toString());
      } else {
        this.appInsightsLoggerService.log('error', 'ERROR saving Partial application. Component: CoverageComponent', '');
        this.submitted = false;
      }
    } catch (error) {
      this.appInsightsLoggerService.log('error', 'ERROR saving Partial application. Component: CoverageComponent. Error: ' + error.message, '');
      this.submitted = false;
    }
  }

  /**
   * Copy the freshly built coverage array onto
   * `mhsApplication.applicant.coverage` so any subsequent
   * submitFinalCloudApplication call serializes the up-to-date selections.
   *
   * Defensive about the shape of `mhsApplication` because in the edit flow the
   * user may have entered the application from a resumed session where
   * `applicant` is already populated, while in the first-time flow it may not
   * yet exist (applicant-info will build it later).
   */
  private syncCoverageIntoMhsApplication(mhsCoverages: CoverageMhs[]): void {
    const session = this.userSessionService.data;
    if (!session || !session.mhsApplication) {
      return;
    }
    if (!session.mhsApplication.applicant) {
      // Don't fabricate an applicant in the first-time flow; applicant-info
      // will create it and copy the coverage at that point.
      return;
    }
    session.mhsApplication.applicant.coverage = mhsCoverages;
  }

  /**
   * Edit-mode save: push the updated applicant (which now carries the new
   * coverage array) to the backend, then return the user to auth-elec-sign
   * with the success toast. If the backend call fails we keep the user on
   * the coverage page and surface the existing error modal so they can retry.
   */
  private persistEditedCoverage(): void {
    const session = this.userSessionService.data;
    const mhsId = session?.mhsId;
    const applicant: ApplicantMhs | undefined = session?.mhsApplication?.applicant;

    // If we don't have an mhsId or applicant yet (e.g. user jumped directly to
    // /coverage?mode=edit in a fresh session) we can't update the backend.
    // Fall back to the original local-only behavior so the user isn't stuck.
    if (!mhsId || !applicant) {
      this.appInsightsLoggerService.log(
        'warn',
        'Coverage edit save: missing mhsId or applicant in session, navigating without backend update.',
        ''
      );
      this.navigateBackToReviewAfterSave();
      return;
    }

    this.isSavingEdit = true;
    const startTime = Date.now();

    this.applicationService
      .updateApplicantWithExStreamCloudApplication(mhsId, applicant)
      .then((response: PartialSaveResp) => {
        const duration = Date.now() - startTime;
        const code = response?.httpStatusCode;
        // 201 = Created/Updated, 304 = Not Modified (treated as success upstream)
        if (code === 201 || code === 304) {
          this.appInsightsLoggerService.log(
            'info',
            `Coverage edit save: applicant updated successfully (status ${code}, ${duration}ms). MhsId: ${mhsId}`,
            ''
          );
          this.isSavingEdit = false;
          this.navigateBackToReviewAfterSave();
        } else {
          this.appInsightsLoggerService.log(
            'error',
            `Coverage edit save: unexpected status ${code} (${duration}ms). MhsId: ${mhsId}`,
            ''
          );
          this.isSavingEdit = false;
          this.showCoverageSaveErrorModal();
        }
      })
      .catch((error: any) => {
        const duration = Date.now() - startTime;
        this.appInsightsLoggerService.log(
          'error',
          `Coverage edit save: applicant update failed (${duration}ms). MhsId: ${mhsId}. Error: ${JSON.stringify(error)}`,
          ''
        );
        this.isSavingEdit = false;
        this.showCoverageSaveErrorModal();
      });
  }

  /**
   * Common post-save navigation: flip the success-toast flag and route back
   * to /review. Extracted so both the "missing mhsId" fallback and
   * the success-path use the exact same behavior.
   */
  private navigateBackToReviewAfterSave(): void {
    this.userSessionService.data['showCoverageSavedToast'] = true;
    sessionStorage.setItem('showCoverageSavedToast', 'true');

    const destinationRoute = EEOI_APP_ROUTES.AUTH_ELEC_SIGN;
    this.router.navigate([destinationRoute], { state: { showSuccess: true } });
    this.appInsightsApiService.startNavigationEvent(destinationRoute.toString());
  }

  /**
   * Surfaces a generic "something went wrong" modal if the backend rejected
   * the coverage update, leaving the user on the coverage edit page so they
   * can retry by clicking Save again.
   */
  private showCoverageSaveErrorModal(): void {
    // Reuse the existing cancel/lose-data modal content for a soft error
    // surface (no new content keys required). If a dedicated error modal is
    // available in the page later, swap it in here.
    this.modalService.showModal('prism-ui-modal-error-modal');
  }

  checkboxChecked(event: Event, key: string){
    // @ts-ignore
    let isChecked = event.detail.checked;

    this.checkIfCheckboxFormValid();

    if(isChecked){//they checked the box
      for(let pp of this.products){
        if(pp.productLine === 'LIFE'){
          if(pp.key === key){
            if(pp.productSchedule != undefined){
              this.coverageForm.get(pp.key).setValue(true);
              this.coverageForm.addControl(pp.key + "_schedule", this.fb.control(''));
              this.coverageForm.get(pp.key).markAsTouched();
              this.checkIfCheckboxFormValid();
            }
          }
        } else {//LTD or STD
          if(pp.key === key){
            this.coverageForm.get(pp.key).setValue(true);
            this.checkIfCheckboxFormValid();
          }
        }
      }
    } else {//they unchecked the box
      for(let pp of this.products){
        if(pp.productLine === 'LIFE'){
          if(pp.key === key){
            if(pp.productSchedule != undefined){
              this.coverageForm.get(pp.key).setValue(false);
              this.checkIfCheckboxFormValid();
            }
          }
        } else {//LTD or STD
          if(pp.key === key){
            this.coverageForm.get(pp.key).setValue(false);
            this.checkIfCheckboxFormValid();
          }
        }
      }
    }
  }

  checkIfCheckboxFormValid(){
    //set the checkboxFormValid to true if there is at least one checkbox checked
    let checked = 0;

    Object.keys(this.coverageForm.controls).forEach(key => {
      const control = this.coverageForm.controls[key];

      if (control.value == true) {
        checked ++;
      }
    });

    if (checked > 0) {
      this.checkboxFormValid = true;
      this.coverageForm.setErrors(null);
    } else {
      this.checkboxFormValid = false;
      this.coverageForm.setErrors( { requireCheckboxesToBeChecked: true });
    }
  }

  leaveAnyway(){
    this.userSessionService.clearSession();
    this.userSessionService.data.sessionEnded = true;
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(['/instructions']);
  }

  onCancelModal(modalName: string){
    this.cancelEmitter.emit();

    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {
      console.error(
        `Modal with id ${modalName} not found or doesn't support close method.`,
      );
    }
  }

  // use the window service to access the modal element
  // and close the modal when the user clicks outside of it
  onClick(event: Event) {
    const modal = this.windowService.nativeDocument.getElementById(
      'prism-ui-modal-cancel-modal'
    );

    if (modal && event.target === modal) {
      this.onCancelModal('prism-ui-modal-cancel-modal');
    }
  }

  canDeactivate(){
    if (!this.submitted && !this.userSessionService.data.sessionEnded){
      this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
      return this.userSessionService.data.sessionEnded;
    } else {
      return of(true);
    }
  }

  onCancel(){
    this.googleAnalyticsService.sendCustomEvent(this.cancelCoverages);
    this.submitted = false;

    this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
  }

  onScrollToTop(){
    this.scroller.setOffset([0, 200]);
    this.scroller.scrollToAnchor('topOfPage');
  }

  private _validate(form: UntypedFormGroup): void {
    form.markAllAsTouched();

    Object.keys(form.controls).forEach(key => {
      const control = form.controls[key] as UntypedFormControl;

      control.updateValueAndValidity({onlySelf: false, emitEvent: true});
    });
  }
}
